import {
  Chip,
  createStyles,
  ListSubheader,
  makeStyles,
  TextField,
  Typography,
} from '@material-ui/core'
import { CSSProperties } from '@material-ui/core/styles/withStyles'
import { Autocomplete } from '@material-ui/lab'
import match from 'autosuggest-highlight/match'
import parse from 'autosuggest-highlight/parse'
import { SaveButton } from 'components/Common/Button/SaveButton'
import { MaterialsOperations } from 'controllers/MaterialsController'
import React, { useRef } from 'react'
import { useAppSelector } from 'store/configureStore'
import {
  IssueCode,
  KeywordDto,
  MaterialSummaryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { projectSelectors } from '../selectors/projectSelectors'
import { bomItemIsPurchasingPartSelector } from '../selectors/bomItemIsPurchasingPartSelector'
import { ProjectEditableStateSelector } from '../selectors/ProjectEditableStateSelector'
import { useMaterialKeywords } from './useMaterialKeywords'
import { isEqual } from 'lodash'

const MaterialKeywordsOptions = (option, state) => {
  const matches = match(option.translatedString || '', state.inputValue)
  const parts = parse(option.translatedString || '', matches)

  return (
    <div>
      {parts.map((part, index) => (
        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
          {part.text}
        </span>
      ))}
    </div>
  )
}

const useStyles = makeStyles(() =>
  createStyles({
    input: {
      minWidth: '0 !important',
    },
  })
)

export const BomItemMaterialKeywordInput = (props: {
  bomItemId: string
  containerStyle?: CSSProperties
  disableLabel?: boolean
  disableUnderline?: boolean
}) => {
  const cn = useStyles()
  const isProjectEditable = useAppSelector(ProjectEditableStateSelector)
  const isBuyerView = useAppSelector(
    projectSelectors.ProjectInBuyerViewSelector
  )
  const isPurchasingPartType = useAppSelector(
    bomItemIsPurchasingPartSelector(props.bomItemId)
  )

  const incompleteMaterialIssue = useAppSelector(
    projectSelectors.bomItemIssueByCodeSelector(
      props.bomItemId,
      IssueCode.MaterialNotSet
    ),
    isEqual
  )

  const {
    t,
    translateKeyword,
    loading,
    materialSummary,
    handleSaveKeywords,
    options,
    open,
    saving,
    setOpen,
    theme,
    handleKeyDown,
    handleClose,
    handleChange,
    isEditing,
  } = useMaterialKeywords(props.bomItemId)

  const autocompleteRef = useRef<HTMLDivElement>(undefined)
  const draggableRef = useRef(undefined)

  return (
    <div
      draggable={isProjectEditable}
      ref={draggableRef}
      style={{ width: '100%', position: 'relative', ...props.containerStyle }}
      onDragStart={(e) => {
        setOpen(false)
        e.dataTransfer.setData('text/plain', JSON.stringify(materialSummary))
      }}
      onDrag={(e) => {
        if (e.target !== draggableRef.current) {
          e.preventDefault()
        }
      }}
      onDrop={(e) => {
        const dropedMaterialSummary = JSON.parse(
          e.dataTransfer.getData('text/plain')
        ) as MaterialSummaryDto

        delete dropedMaterialSummary.tokens['dimensions']
        delete dropedMaterialSummary.tokens['shape']

        handleChange(
          null,
          Object.values({
            ...materialSummary.tokens,
            ...dropedMaterialSummary.tokens,
          }).flat(),
          'select-option'
        )
        autocompleteRef.current.querySelector('input').focus()
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {props.disableLabel ? undefined : (
        <Typography
          variant="caption"
          color="textSecondary"
          component="label"
          className="MuiInputLabel-shrink"
        >
          {materialSummary?.id !== '' && materialSummary?.description
            ? `${materialSummary.id}: ${materialSummary.description}`
            : t('common:material-keywords', 'material keywords')}
        </Typography>
      )}

      <Autocomplete<KeywordDto, true, true, false>
        classes={{
          input: cn.input,
        }}
        disabled={!isProjectEditable}
        ref={autocompleteRef}
        multiple
        freeSolo={false}
        disableClearable={true}
        loading={
          loading[MaterialsOperations.GetMaterials] ||
          saving?.requestStatus === 'pending'
        }
        loadingText={t('common:loading')}
        open={open}
        onDoubleClick={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        onOpen={() => setOpen(true)}
        onClose={() => handleClose()}
        options={options || []}
        onBlur={() => handleSaveKeywords(materialSummary)}
        value={Object.values(materialSummary?.tokens || {}).flat()}
        getOptionLabel={(option) => translateKeyword(option)}
        groupBy={(option) => option.category}
        onKeyDown={handleKeyDown}
        openOnFocus
        fullWidth
        noOptionsText={t(
          'common:material-tokens-not-found',
          'no materials keywords available'
        )}
        autoHighlight
        autoSelect
        disableCloseOnSelect
        selectOnFocus
        onChange={handleChange}
        filterSelectedOptions
        getOptionSelected={(option, value) => {
          return option.originalKeyword === value.originalKeyword
        }}
        renderGroup={(params) => {
          return [
            <ListSubheader
              key={params.key}
              style={{
                zIndex: 2,
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
                borderTop: `1px solid ${theme.palette.divider}`,
              }}
            >
              {t(`project:material-category-${params.group}`, params.group)}
            </ListSubheader>,
            params.children,
          ]
        }}
        renderTags={(options) => {
          return options.map((token) => {
            return (
              <Chip
                key={token.originalKeyword}
                label={translateKeyword(token)}
                variant="outlined"
                size="small"
                style={{
                  margin: '3px 2px 4px 0',
                }}
              />
            )
          })
        }}
        renderOption={MaterialKeywordsOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={autocompleteRef}
            error={
              !isBuyerView &&
              !isPurchasingPartType &&
              materialSummary?.id === ''
            }
            helperText={
              incompleteMaterialIssue &&
              t(
                'project:incomplete-material-input',
                'select a $t(project:material-category-materialgroup), $t(project:material-category-quality)'
              )
            }
            disabled={!isProjectEditable}
            onClick={(e) => e.stopPropagation()}
            InputProps={{
              ...params.InputProps,
              disableUnderline: !isProjectEditable || props.disableUnderline,
              endAdornment:
                isEditing || saving?.requestStatus === 'pending' ? (
                  <>
                    <SaveButton
                      translationKey="common:apply"
                      size="small"
                      saving={saving?.requestStatus === 'pending'}
                      variant="text"
                      onClick={(e) => {
                        e.stopPropagation()
                        handleSaveKeywords(materialSummary)
                      }}
                      style={{
                        marginLeft: 'auto',
                      }}
                    />
                    {params.InputProps.endAdornment}
                  </>
                ) : (
                  params.InputProps.endAdornment
                ),
            }}
          />
        )}
      />
    </div>
  )
}
